import { Injectable } from '@angular/core';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';
import { forkJoin, takeUntil, finalize } from 'rxjs';
import { AuthService } from '../../../../../shared/services/auth.service';
import { WorkspaceService } from '../../../../../shared/services/workspace.service';
import { EmitterService } from '../../../../../shared/services/emitter.service';
import { ApiError, User, Workspace, WorkspaceUpdatedEvent } from '../../../../../shared/interfaces';
import { OverlayService } from '../../../../../shared/services/overlayService';
import { AnalyticsService } from '../../../../../shared/services/analytics.service';
import { UntypedFormGroup } from '@angular/forms';
import {
  UserWorkspaceRole,
  SaveWorkspaceResult,
  AddUserToWorkspaceParams,
  InitWorkspaceUsersParams,
  SaveWorkspaceParams,
} from '../../../../models/workspace.model';
import { TRANSLATION_KEYS, UI, ERROR_CODES, STATES, WORKSPACE_KEYS } from '../../../constants/workspace.constants';
import { StringUtilsService } from '../../../../../shared/services/string-utils.service';

@Injectable({ providedIn: 'root' })
export class WorkspacePermissionHelperService {
  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly authService: AuthService,
    private readonly workspaceService: WorkspaceService,
    private readonly overlayService: OverlayService,
    private readonly stringUtilsService: StringUtilsService,
    private readonly analyticsService: AnalyticsService
  ) {}

  getHeaderText(colField: string): string {
    return this.translatePipe.transform(this.mapHeaderText(colField));
  }

  matchesFilter(value: string | undefined, filter: string | undefined, normalize: boolean = false): boolean {
    if (!filter) return true;
    if (!value) return false;

    return normalize
      ? this.stringUtilsService.getNormalizedName(value).includes(this.stringUtilsService.getNormalizedName(filter))
      : value.toLowerCase().includes(filter.toLowerCase());
  }

  saveUsersToWorkspace(users: any[], workspaceId: string, ngUnsubscribe: any) {
    const authUser = this.authService.getAuthUser();
    const observables = users.map((user) =>
      this.workspaceService.addUserToWorkspace(authUser.company.id, workspaceId, user.username)
    );
    return forkJoin(observables).pipe(takeUntil(ngUnsubscribe));
  }

  async saveWorkspace(params: SaveWorkspaceParams): Promise<SaveWorkspaceResult> {
    this.prepareWorkspaceForm(params);
    if (params.form.invalid) {
      this.handleInvalidForm(params.form);
      return { success: false };
    }

    const updatedData = { ...params.workspace, ...params.form.value };
    const brandId = this.authService.getCurrentBrandId();

    return new Promise((resolve) => {
      this.workspaceService
        .editWorkspace(updatedData.id, updatedData, brandId)
        .pipe(
          finalize(() => {
            params.isPending = false;
          }),
          takeUntil(params.ngUnsubscribe)
        )
        .subscribe({
          next: (result: any) => {
            this.handleWorkspaceSaveSuccess(result, updatedData, params.workspace, resolve);
          },
          error: (error) => {
            this.handleWorkspaceSaveError(error, updatedData, resolve);
          },
        });
    });
  }

  async addUserToWorkspace(params: AddUserToWorkspaceParams): Promise<any[]> {
    const { username } = params.formData;
    const companyUser = params.companyUsers.find((u) => u.username === params.userData?.username);
    const newUser = companyUser
      ? this.buildExistingCompanyUser(companyUser, username, params)
      : this.buildPendingCompanyUser(username, params);

    const authUser = this.authService.getAuthUser();
    return new Promise((resolve) => {
      this.workspaceService
        .addUserToWorkspace(authUser.company.id, params.workspaceId, newUser.username)
        .pipe(takeUntil(params.ngUnsubscribe))
        .subscribe({
          next: () => {
            params.users.push(newUser);
            resolve(params.users);
          },
          error: () => {
            this.overlayService.showError(
              this.translatePipe.transform(TRANSLATION_KEYS.WORKSPACE_ADD_USER_FAILED),
              UI.HEADER_ERROR_TITLE
            );
            resolve(params.users);
          },
        });
    });
  }

  // Rows come straight from GET /api/workspace/{companyId}/{workspaceId}/users
  // (workspace.usernames is no longer serialized by the backend).
  initWorkspaceUsers(params: InitWorkspaceUsersParams): any[] {
    if (!params.companyUsers) return [];
    return params.companyUsers.map((user: User) => this.buildWorkspaceUser(user, params)).filter(Boolean) as any[];
  }

  private mapHeaderText(colField: string): string {
    switch (colField) {
      case STATES.FULL_NAME:
        return STATES.USER;
      case STATES.USERNAME:
        return STATES.EMAIL;
      case STATES.FIRST_NAME:
        return STATES.FIRST_NAME;
      case STATES.LAST_NAME:
        return STATES.LAST_NAME;
      case STATES.ACTION:
        return STATES.ACTION;
      default:
        return colField;
    }
  }

  private prepareWorkspaceForm(params: any) {
    params.form.patchValue({
      name: params.trimString(params.form.value.name),
    });
  }

  private handleInvalidForm(form: UntypedFormGroup) {
    form.markAllAsTouched();
    const hasHtmlError = Object.keys(form.controls).some((key) =>
      form.controls[key].hasError(WORKSPACE_KEYS.HTML_NOT_ALLOWED)
    );
    const message = hasHtmlError ? TRANSLATION_KEYS.VALIDATION_FAILED : TRANSLATION_KEYS.MANDATORY_FIELD_REQUIRED;
    this.overlayService.showError(this.translatePipe.transform(message), UI.HEADER_ERROR_TITLE);
  }

  private handleWorkspaceSaveSuccess(
    result: Workspace,
    updatedData: Workspace,
    workspace: Workspace,
    resolve: Function
  ) {
    EmitterService.get(WorkspaceUpdatedEvent).emit(result);
    this.analyticsService.clearAnalyticsCache();
    this.overlayService.showSuccess(
      this.translatePipe.transform(TRANSLATION_KEYS.OVERLAY_CHANGE_SAVE),
      UI.HEADER_SUCCESS_TITLE
    );
    resolve({ success: true, updatedWorkspace: { ...workspace, name: updatedData.name } });
  }

  private handleWorkspaceSaveError(error: ApiError, updatedData: Workspace, resolve: Function) {
    const { errorCode } = error;
    const errorMessage =
      errorCode === ERROR_CODES.DUPLICATE_NAME
        ? TRANSLATION_KEYS.WORKSPACE_EDIT_DUPLICATE_ERROR
        : TRANSLATION_KEYS.WORKSPACE_EDIT_ERROR;

    this.overlayService.showError(
      this.translatePipe.transform(errorMessage, { name: updatedData.name }),
      UI.HEADER_ERROR_TITLE
    );
    resolve({ success: false });
  }

  private buildExistingCompanyUser(companyUser: User, username: string, params: any): UserWorkspaceRole {
    const statusInfo = params.activationStatuses.find((s: any) => s.code === companyUser.activationStatus);
    return {
      user: companyUser,
      fullName: companyUser.fullName,
      username,
      jobTitle: companyUser.jobTitle,
      status: statusInfo,
      firstName: companyUser.firstName,
      lastName: companyUser.lastName,
    };
  }

  private buildPendingCompanyUser(username: string, params: any): UserWorkspaceRole {
    const statusInfo = params.activationStatuses.find((s: any) => s.code === STATES.PENDING_INPROGRESS);
    return {
      fullName: '',
      username,
      jobTitle: '',
      status: statusInfo,
      firstName: '',
      lastName: '',
    };
  }

  private buildWorkspaceUser(user: User, params: any): UserWorkspaceRole {
    if (!user) return null;

    const statusInfo = params.activationStatuses.find((s: any) => s.code === user.activationStatus);

    return {
      user,
      fullName: user.fullName ? user.fullName.trim() : '',
      username: user.username,
      jobTitle: user.jobTitle,
      status: statusInfo,
      firstName: user.firstName,
      lastName: user.lastName,
    };
  }
}
